import React, { useEffect, useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Check } from 'lucide-react';

interface GoalData {
  name: string;
  progress: number;
  color: string;
}

interface GoalProgressRingProps {
  goals: GoalData[];
  size?: number;
  strokeWidth?: number;
  translations?: {
    alignment: string;
    addGoals: string;
    trending: string;
    showMore?: string;
    showLess?: string;
    selectGoals?: string;
  };
}

const defaultTranslations = {
  alignment: 'Allineamento',
  addGoals: 'Aggiungi i tuoi obiettivi',
  trending: 'Tendenza',
  showMore: 'Mostra altri',
  showLess: 'Mostra meno',
  selectGoals: 'Seleziona obiettivi'
};

const GoalProgressRing: React.FC<GoalProgressRingProps> = ({
  goals,
  size = 200,
  strokeWidth = 12,
  translations = defaultTranslations
}) => {
  const t = { ...defaultTranslations, ...translations };

  // Track which goals are selected (indices) - default to first 3
  const [selectedIndices, setSelectedIndices] = useState<number[]>(() => {
    return goals.slice(0, 3).map((_, i) => i);
  });

  // Track if selector is expanded
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset selection when goals change
  useEffect(() => {
    setSelectedIndices(goals.slice(0, 3).map((_, i) => i));
  }, [goals.length]);

  // Get the selected goals based on indices
  const selectedGoals = useMemo(() => {
    return selectedIndices
      .filter(i => i < goals.length)
      .map(i => goals[i])
      .slice(0, 3); // Max 3 for display
  }, [selectedIndices, goals]);

  const [animatedProgress, setAnimatedProgress] = useState<number[]>(selectedGoals.map(() => 0));

  const center = size / 2;
  const baseRadius = (size - strokeWidth * Math.min(selectedGoals.length, 3) * 2) / 2;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAnimatedProgress(selectedGoals.map(g => g.progress));
    }, 300);
    return () => clearTimeout(timeout);
  }, [selectedGoals]);

  const colorPalette = [
    { stroke: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' },
    { stroke: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)' },
    { stroke: '#a855f7', bg: 'rgba(168, 85, 247, 0.1)' },
    { stroke: '#ec4899', bg: 'rgba(236, 72, 153, 0.1)' },
    { stroke: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' }
  ];

  const avgProgress = useMemo(() => {
    if (selectedGoals.length === 0) return 0;
    return Math.round(selectedGoals.reduce((acc, g) => acc + g.progress, 0) / selectedGoals.length);
  }, [selectedGoals]);

  const trend = useMemo(() => {
    if (avgProgress >= 60) return 'up';
    if (avgProgress <= 40) return 'down';
    return 'stable';
  }, [avgProgress]);

  const toggleGoalSelection = (index: number) => {
    setSelectedIndices(prev => {
      if (prev.includes(index)) {
        // Don't allow deselecting if it would leave us with 0 selected
        if (prev.length <= 1) return prev;
        return prev.filter(i => i !== index);
      } else {
        // Max 3 selected
        if (prev.length >= 3) {
          // Replace the first selected with new one
          return [...prev.slice(1), index];
        }
        return [...prev, index];
      }
    });
  };

  if (goals.length === 0) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl md:text-3xl">🎯</span>
          </div>
          <p className="text-[12px] md:text-[13px] text-black/40 font-medium">{t.addGoals}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Ring chart container with fixed dimensions */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {selectedGoals.map((goal, index) => {
            const radius = baseRadius - (index * (strokeWidth + 6));
            const circumference = 2 * Math.PI * radius;
            const progress = animatedProgress[index] || 0;
            const dashOffset = circumference - (progress / 100) * circumference;
            const colors = colorPalette[selectedIndices[index] % colorPalette.length];

            return (
              <g key={index}>
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={colors.bg}
                  strokeWidth={strokeWidth}
                />
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={colors.stroke}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className="transition-all duration-1000 ease-out"
                  style={{
                    filter: `drop-shadow(0 0 4px ${colors.stroke}40)`
                  }}
                />
              </g>
            );
          })}
        </svg>

        {/* Center percentage indicator - now contained within ring bounds */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center bg-white/90 backdrop-blur-sm rounded-full px-5 py-4 shadow-sm">
            <div className="flex items-center justify-center gap-1.5">
              {trend === 'up' && <TrendingUp size={18} className="text-emerald-500" />}
              {trend === 'down' && <TrendingDown size={18} className="text-rose-500" />}
              {trend === 'stable' && <Minus size={18} className="text-amber-500" />}
              <span className={`text-[32px] md:text-[36px] font-black leading-none ${
                trend === 'up' ? 'text-emerald-600' :
                trend === 'down' ? 'text-rose-500' : 'text-amber-600'
              }`}>
                {avgProgress}%
              </span>
            </div>
            <p className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider mt-1">
              {t.alignment}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 w-full max-w-[320px]">
        <div className="space-y-3">
          {selectedGoals.map((goal, index) => {
            const colors = colorPalette[selectedIndices[index] % colorPalette.length];
            const progress = Math.round(goal.progress || 0);
            return (
              <div key={index} className="flex items-start gap-3 px-3 py-3 bg-gray-50/50 rounded-xl">
                <div
                  className="w-3 h-3 rounded-full shrink-0 mt-0.5"
                  style={{ backgroundColor: colors.stroke }}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] md:text-[13px] font-medium text-black/70 leading-snug block">
                    {goal.name}
                  </span>
                </div>
                <span
                  className="text-[12px] md:text-[13px] font-bold shrink-0 px-3 py-1 rounded-full whitespace-nowrap"
                  style={{
                    backgroundColor: `${colors.stroke}15`,
                    color: colors.stroke
                  }}
                >
                  {progress}%
                </span>
              </div>
            );
          })}
        </div>

        {/* Goal Selector - only show if more than 3 goals available */}
        {goals.length > 3 && (
          <div className="mt-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              <span className="text-[12px] md:text-[13px] font-semibold text-black/60">
                {isExpanded ? t.showLess : `${t.showMore} (${goals.length - 3})`}
              </span>
              {isExpanded ? (
                <ChevronUp size={16} className="text-black/40" />
              ) : (
                <ChevronDown size={16} className="text-black/40" />
              )}
            </button>

            {isExpanded && (
              <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <p className="text-[10px] md:text-[11px] font-bold text-black/40 uppercase tracking-wider mb-3">
                  {t.selectGoals}
                </p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {goals.map((goal, index) => {
                    const isSelected = selectedIndices.includes(index);
                    const colors = colorPalette[index % colorPalette.length];
                    return (
                      <button
                        key={index}
                        onClick={() => toggleGoalSelection(index)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isSelected
                            ? 'bg-white shadow-sm border border-gray-200'
                            : 'bg-transparent hover:bg-white/50'
                        }`}
                      >
                        <div
                          className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
                            isSelected
                              ? 'bg-opacity-100'
                              : 'bg-opacity-20'
                          }`}
                          style={{
                            backgroundColor: isSelected ? colors.stroke : `${colors.stroke}30`
                          }}
                        >
                          {isSelected && <Check size={12} className="text-white" />}
                        </div>
                        <span className={`text-[12px] md:text-[13px] font-medium text-left flex-1 ${
                          isSelected ? 'text-black/80' : 'text-black/50'
                        }`}>
                          {goal.name}
                        </span>
                        <span
                          className="text-[11px] font-bold shrink-0 px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: `${colors.stroke}15`,
                            color: colors.stroke
                          }}
                        >
                          {Math.round(goal.progress)}%
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-black/30 mt-2 text-center">
                  {selectedIndices.length}/3
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalProgressRing;
